package com.tovtalk.ui.platform

import com.tovtalk.domain.entity.LoginMethod
import com.tovtalk.domain.entity.DomainResult
import com.tovtalk.domain.repository.AuthManager
import com.tovtalk.domain.repository.UserRepository
import com.tovtalk.utils.logger.Logger
import kotlinx.browser.window
import org.koin.core.context.GlobalContext

/** Only run redirect check once per page load – avoids repeated sign-in flow. */
private var redirectCheckRunThisPageLoad: Boolean = false

/**
 * Run before UI: consume Firebase redirect result and login to backend so tokens are saved.
 * getRedirectResult() must run as early as possible or Firebase may not return the result.
 */
actual suspend fun completeGoogleRedirectAndSaveTokensIfAny() {
    Logger.d("GoogleRedirect: completeGoogleRedirectAndSaveTokensIfAny ENTER (before UI)", "GoogleRedirect")
    val authManager = GlobalContext.get().get<AuthManager>()
    val userRepository = GlobalContext.get().get<UserRepository>()
    when (val r = authManager.getRedirectResultFirebaseTokenIfAny()) {
        is DomainResult.Success -> {
            Logger.d("GoogleRedirect: completeGoogleRedirectAndSaveTokensIfAny SUCCESS -> login to backend", "GoogleRedirect")
            try {
                window.history.replaceState(null, "", window.location.origin + window.location.pathname + window.location.search)
            } catch (_: Throwable) { }
            when (val loginResult = userRepository.login(r.data, null, LoginMethod.GOOGLE, com.tovtalk.utils.config.isHadranDevice(), null)) {
                is DomainResult.Success -> Logger.d("GoogleRedirect: completeGoogleRedirectAndSaveTokensIfAny backend login OK", "GoogleRedirect")
                is DomainResult.Error -> Logger.e("GoogleRedirect: completeGoogleRedirectAndSaveTokensIfAny backend login failed: ${loginResult.message}", "GoogleRedirect")
                else -> {}
            }
        }
        else -> Logger.d("GoogleRedirect: completeGoogleRedirectAndSaveTokensIfAny no redirect result", "GoogleRedirect")
    }
}

actual suspend fun tryCompleteGoogleRedirectLoginIfAny(onToken: suspend (String) -> Unit) {
    Logger.d("GoogleRedirect: tryCompleteGoogleRedirectLoginIfAny ENTER redirectCheckRunThisPageLoad=$redirectCheckRunThisPageLoad", "GoogleRedirect")
    if (redirectCheckRunThisPageLoad) {
        Logger.d("GoogleRedirect: tryCompleteGoogleRedirectLoginIfAny SKIP (already ran this page load)", "GoogleRedirect")
        return
    }
    redirectCheckRunThisPageLoad = true
    Logger.d("GoogleRedirect: tryCompleteGoogleRedirectLoginIfAny calling getRedirectResultFirebaseTokenIfAny", "GoogleRedirect")
    val authManager = GlobalContext.get().get<AuthManager>()
    when (val r = authManager.getRedirectResultFirebaseTokenIfAny()) {
        is DomainResult.Success -> {
            Logger.d("GoogleRedirect: tryCompleteGoogleRedirectLoginIfAny SUCCESS -> clearing URL and calling onToken", "GoogleRedirect")
            try {
                window.history.replaceState(null, "", window.location.origin + window.location.pathname + window.location.search)
            } catch (_: Throwable) { }
            onToken(r.data)
        }
        else -> {
            Logger.d("GoogleRedirect: tryCompleteGoogleRedirectLoginIfAny no token (null or not Success)", "GoogleRedirect")
        }
    }
}
